import {
    useLayoutEffect,
    useCallback,
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    Animated,
    Platform,
    type View
} from "react-native";
import type INavigationMenuPanelProps from "./type";
import type {
    NavigationMenuPanelRef
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useOverlayAccessibility,
    useAnchoredPosition,
    POPOVER_PORTAL_NAME
} from "../../../../helpers/overlay";
import {
    usePopoverSurfaceMotion
} from "../../../popoverSurface/hook";
import type {
    RefForwardingComponent
} from "../../../../types";
import {
    POPOVER_NO_COLLISION_PADDING,
    getPopoverElement
} from "../../../popover/util";
import {
    getPopoverSurfaceOrigin
} from "../../../popoverSurface/stylesheet";
import {
    Portal
} from "../../../../helpers/portalize";

const NavigationMenuPanel: RefForwardingComponent<NavigationMenuPanelRef, INavigationMenuPanelProps> = ({
    customTheme,
    anchorRef,
    onDismiss,
    children,
    isOpen,
    offset,
    style,
    ...props
}, ref) => {
    const {
        shadowAlpha,
        elevations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const {
        width: windowWidth
    } = useWindowDimensions();

    const [
        isMounted,
        setIsMounted
    ] = useState(false);

    const surfaceRef = useRef<View>(null);

    const onDismissRef = useRef(onDismiss);

    const isRendered = isOpen || isMounted;

    const {
        onContentLayout,
        isPositioned,
        measure,
        top
    } = useAnchoredPosition({
        collisionPadding: POPOVER_NO_COLLISION_PADDING,
        isActive: isRendered,
        align: "start",
        side: "bottom",
        customTheme,
        anchorRef,
        offset
    });

    const isShow = isOpen && isPositioned;

    const handleExited = () => {
        setIsMounted(false);
    };

    const {
        opacity,
        motion
    } = usePopoverSurfaceMotion({
        onExited: handleExited,
        isReady: isPositioned,
        actualSide: "bottom",
        customTheme,
        isRendered,
        isOpen
    });

    const {
        panel: panelDynamicStyle
    } = useStyles({
        width: windowWidth,
        shadowAlpha,
        elevations,
        borders,
        isShow,
        colors,
        spaces
    });

    const {
        containerProps
    } = useOverlayAccessibility({
        containerRef: surfaceRef,
        isAutoFocused: false,
        isActive: isOpen,
        onDismiss
    });

    const handleSurfaceRef = useCallback((node: View | null) => {
        surfaceRef.current = node;

        if(typeof ref === "function") {
            ref(node);

            return;
        }

        if(ref) {
            ref.current = node;
        }
    }, [
        ref
    ]);

    useLayoutEffect(() => {
        onDismissRef.current = onDismiss;
    });

    useEffect(() => {
        if(isOpen) {
            setIsMounted(true);
        }
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isRendered) {
            measure();
        }
    });

    useEffect(() => {
        if(!isOpen || Platform.OS !== "web" || typeof document === "undefined") {
            return;
        }

        const onPointerDown = (event: Event) => {
            const {
                target
            } = event;

            if(!(target instanceof Node)) {
                return;
            }

            const surfaceElement = getPopoverElement(surfaceRef.current);

            const anchorElement = getPopoverElement(anchorRef.current);

            if(surfaceElement?.contains(target) || anchorElement?.contains(target)) {
                return;
            }

            onDismissRef.current?.();
        };

        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [
        anchorRef,
        isOpen
    ]);

    if(!isRendered) {
        return null;
    }

    return <Portal
        name={POPOVER_PORTAL_NAME}
    >
        <Animated.View
            {...props}
            {...containerProps}
            style={[
                style,
                stylesheet.panel,
                panelDynamicStyle,
                {
                    transformOrigin: getPopoverSurfaceOrigin({
                        actualAlign: "center",
                        actualSide: "bottom",
                        direction
                    }),
                    transform: [
                        {
                            translateY: top
                        },
                        {
                            translateY: motion.translateY
                        },
                        {
                            scale: motion.scale
                        }
                    ],
                    opacity
                }
            ]}
            onLayout={onContentLayout}
            ref={handleSurfaceRef}
        >
            {children}
        </Animated.View>
    </Portal>;
};
export default forwardRef(NavigationMenuPanel);
